<template>
    <div>
        <q-card class="calculator-card card-shadow">
            <q-card-section class="bg-form-bar text-white q-py-xs">
                <rate-monitor
                    v-if="current_ramp"
                    :ramp="current_ramp"
                    :rate-reversed="rate_reversed"
                    :discounted-displayable-rate="displayable_discounted_rate"
                    :undiscounted-displayable-rate="displayable_rate"
                    :update-date-time="update_datetime"
                />
            </q-card-section>

            <q-card-section class="q-pb-none">
                <div class="row q-col-gutter-lg">
                    <div class="col-12">
                        <origin-input-box
                            v-model="origin_amount"
                            :min-transfer-amount="min_transfer_amount"
                            :max-transfer-amount="max_transfer_amount"
                            @change="calculateDestAmount"
                            @originChanged="changeRamp"
                            :error="error"
                        />
                    </div>
                    <div class="col-12 q-pt-sm">
                        <dest-input-box
                            v-model="dest_amount"
                            @change="calculateOriginAmount"
                            :origin-currency="origin_currency"
                            @destChanged="changeRamp"
                        />
                    </div>
                </div>
            </q-card-section>

            <q-card-section v-if="discounted_rate_activated" class="q-pb-none">
                <tiered-discount
                    v-model="discount_type"
                    :dest-amount="dest_amount"
                    :discounted-dest-amount="discounted_dest_amount"
                    :dest-currency="to_currency_symbol"
                    :origin-amount="origin_amount"
                    :discounted-origin-amount="discounted_origin_amount"
                    :origin-currency="from_currency_symbol"
                    :rate-reversed="rate_reversed"
                    :discounted-displayable-rate="displayable_discounted_rate"
                    @showTiers="displayDiscountRules = true"
                />
            </q-card-section>

            <q-card-section>
                <calculation-details
                    :dest-amount="dest_amount"
                    :dest-currency="to_currency_symbol"
                    :origin-amount="origin_amount"
                    :discounted-origin-amount="discounted_origin_amount"
                    :discount-type="discount_type"
                    :origin-currency="from_currency_symbol"
                    :is-introduced="isIntroduced"
                    :reward="reward"
                    :discount="discount"
                    :has-welcome-reward="!!userRewards.welcome_reward"
                    :mainRate="displayable_rate"
                    :discountedRate="displayable_discounted_rate"
                    :has-tier="tiers.length > 0"
                    :fees="fees"
                    :total-to-pay="totalToPay"
                    :transfer-time="transfer_time"
                    :is-user-verified="isUserVerified"
                    :discounted-rate-activated="discounted_rate_activated"
                    :account-age="accountAge"
                    :reward-min-transfer-amount="rewardMinTransferAmount"
                    :recipient-gets="recipient_gets"
                    @showDisclaimer="displayDisclaimer = true"
                    @showDiscount="displayDiscountRules = true"
                    @typeChanged="typeChanged"
                />
            </q-card-section>
        </q-card>

        <disclaimer-dialog v-model="displayDisclaimer" />

        <discount-rules-dialog
            v-model="displayDiscountRules"
            :ramp="current_ramp"
            :rate-reversed="rate_reversed"
            v-if="current_ramp"
        />
    </div>
</template>
<script>

import {mapGetters} from 'vuex'
import RateMonitor from "./RateMonitor"
import DestInputBox from "./DestInputBox"
import OriginInputBox from "./OriginInputBox"
import CalculationDetails from "./CalculationDetails"
import DisclaimerDialog from "./Dialogs/DisclaimerDialog"
import DiscountRulesDialog from "./Dialogs/DiscountRulesDialog"
import TieredDiscount from "./TieredDiscount.vue";

export default {
    name: "Calculator",
    props: {
        exchangeRatesUpdated: {
            type: Number,
            default: 0
        },
        userRewards: {
            type: Object,
            default: () => []
        },
        rewardMinTransferAmount: {
            type: Number,
            default: 0
        },
        userCurrency: {
            type: String,
            default: 'NZD'
        },
        isIntroduced: {
            type: Boolean,
            default: false
        },
        accountAge: {
            type: Number,
            default: 0
        },
        error: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        ...mapGetters(['exchange_rates', 'isUserVerified']),
        current_ramp() {
            return this.exchange_rates.find(rate => (rate.from.acronym === this.origin_currency) && (rate.to.acronym === this.dest_currency))
        },
        update_datetime() {
            return this.exchange_rates.updated_at
        },
        tiers() {
            return this.current_ramp ? this.current_ramp.tiers : []
        },
        current_tier(amount) {
            return (amount) => {
                for (let i = this.tiers.length - 1; i >= 0; i--) {
                    if (amount >= this.tiers[i].amount) {
                        return this.tiers[i]
                    }
                }
                return null
            }
        },
        current_rate() {
            const tier = this.current_tier(this.origin_amount)
            const rate = (tier ? tier.rate : (this.current_ramp ? this.current_ramp.rate : 0))

            return parseFloat(rate)
        },
        main_rate() {
            return parseFloat(this.current_ramp ? this.current_ramp.rate : 0)
        },
        rate_reversed() {
            return this.current_rate < 0.1
        },
        displayable_rate() {
            return Number((this.rate_reversed && this.main_rate) ? (1 / this.main_rate) : this.main_rate).roundToX(this.round_decimals)
        },
        displayable_discounted_rate() {
            return Number((this.rate_reversed && this.current_rate) ? (1 / this.current_rate) : this.current_rate).roundToX(this.round_decimals)
        },
        discounted_origin_amount() {
            return this.rate_reversed ?
             Number(this.dest_amount * this.displayable_discounted_rate).roundToX(this.from_currency_decimals) :
             Number(this.dest_amount / this.displayable_discounted_rate).roundToX(this.from_currency_decimals)
        },
        discounted_dest_amount () {
            return this.rate_reversed ?
              Number(this.origin_amount / this.displayable_discounted_rate).roundToX(this.to_currency_decimals) :
              Number(this.origin_amount * this.displayable_discounted_rate).roundToX(this.to_currency_decimals)
        },
        fees() {
            return this.current_ramp ? parseFloat(this.current_ramp.fees) : 0
        },
        from_currency_symbol() {
            return this.current_ramp ? this.current_ramp.from.symbol : ''
        },
        from_currency_decimals() {
            return this.current_ramp ? this.current_ramp.from.decimals : 2
        },
        to_currency_symbol() {
            return this.current_ramp ? this.current_ramp.to.symbol : ''
        },
        to_currency_decimals() {
            return this.current_ramp ? this.current_ramp.to.decimals : 2
        },
        min_transfer_amount() {
            return this.current_ramp ? this.current_ramp.min_transfer_amount : 0
        },
        max_transfer_amount() {
            return this.current_ramp ? this.current_ramp.max_transfer_amount : 0
        },
        transfer_time() {
            return this.current_ramp ? this.current_ramp.transfer_time : '2 hours to 2 working days'
        },
        current_ramp_welcome_reward() {
            return this.current_ramp ? this.current_ramp.welcome_reward : 0
        },
        unused_rewards() {
            return this.userRewards.unused
        },
        reward() {
            return this.unused_rewards.find(reward => reward.currency === this.userCurrency) || {}
        },
        discount() {
            if (this.reward && (this.reward.currency === this.origin_currency) && (this.origin_amount >= this.rewardMinTransferAmount)) {
                return Math.min(parseFloat(this.reward.total), this.fees + parseFloat(this.origin_amount))
            } else if (!this.isIntroduced && this.userRewards.welcome_reward) {
                return parseFloat(this.current_ramp_welcome_reward)
            } else {
                return 0
            }
        },
        totalToPay() {
            const origin_amount = this.discount_type === 'destination' ? this.discounted_origin_amount : this.origin_amount
            return Number(this.fees + parseFloat(origin_amount) - this.discount)
        },
        recipient_gets() {
            if (this.discount_type === 'origin') {
                return this.discounted_dest_amount
            }

            const value = Number(this.discount_type === 'destination' || this.calculation_side === 'origin' ? this.dest_amount : this.discounted_dest_amount)
            return isFinite(value) ? value : 0
        },
        round_decimals() {
            return this.current_ramp ? this.current_ramp.decimals : 2
        },
        discounted_rate_activated() {
            return this.displayable_discounted_rate !== this.displayable_rate
        }
    },
    data() {
        return {
            origin_currency: 'NZD',
            origin_amount: 1000,
            calculation_side: 'dest',
            dest_currency: '',
            dest_amount: 0,
            ramp: null,
            displayDisclaimer: false,
            displayDiscountRules: false,
            discount_type: null
        }
    },
    methods: {
        changeRamp(route_info) {
            this.origin_currency = route_info.origin_dest === 'origin' ? route_info.currency : this.origin_currency
            this.dest_currency = route_info.origin_dest === 'dest' ? route_info.currency : this.dest_currency
            //this.setDefaultAmount()
            this.calculateDestAmount()
        },
        setDefaultAmount() {
            this.origin_amount = this.current_ramp ? this.current_ramp.min_transfer_amount : this.origin_amount
        },
        calculateOriginAmount() {
            this.calculation_side = 'origin'
            this.origin_amount = this.rate_reversed ?
                Number(this.dest_amount * this.displayable_rate).roundToX(this.from_currency_decimals) :
                Number(this.dest_amount / this.displayable_rate).roundToX(this.from_currency_decimals)
        },
        calculateDestAmount() {
            this.calculation_side = 'dest'
            this.dest_amount = this.rate_reversed && this.displayable_rate ?
                Number(this.origin_amount / this.displayable_rate).roundToX(this.to_currency_decimals) :
                Number(this.origin_amount * this.displayable_rate).roundToX(this.to_currency_decimals)
        },
        typeChanged(value) {
            this.discount_type = value
        }
    },
    mounted() {
        this.setDefaultAmount()
        this.calculateDestAmount()
    },
    updated() {
        this.$emit('result', {
            origin_amount: parseFloat(this.origin_amount),
            dest_amount: parseFloat(this.dest_amount),
            discount: parseFloat(this.discount),
            ramp: this.current_ramp,
            rate: this.main_rate,
            current_rate: this.current_rate,
            total: this.totalToPay,
            transfer_time: this.transfer_time,
            discount_type: this.discount_type,
            calculation_side: this.calculation_side
        })
    },
    watch: {
        exchange_rates(val) {
            this.calculateDestAmount()
        },
        discounted_rate_activated (val) {
            if (!val) {
                this.discount_type = null
            }
        }
    },
    components: {
        TieredDiscount,
        DiscountRulesDialog,
        DisclaimerDialog,
        RateMonitor,
        DestInputBox,
        OriginInputBox,
        CalculationDetails
    },
}
</script>
<style lang="sass">
.calculator-card
    width: 100%
    max-width: 450px
.input-bold
    font-size: 1.3rem
</style>
